import React, { useMemo, useState } from "react";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  makeStyles,
  Typography,
} from "@material-ui/core";

import { useAlert } from "react-alert";

import {
  WebTracker,
  WebTrackerReportTrackerStats,
  WebTrackerResolutionType,
  firstFoundDomain,
  hasVideoProxy,
  isMaps,
} from "../types";
import useAddWebTrackerResolution from "../mutations/useAddWebTrackerResolution";
import TrackerResolutionContent from "./TrackerResolution/TrackerResolutionContent";
import TrackerResolutionExplanation from "./TrackerResolution/TrackerResolutionExplanation";

import { useAllSaasDestinations } from "../../hooks/useAllSaasDestinations";
import {
  RequestableSaasDestination,
  SaaSDestination,
} from "../../destinations/destinations";
import useFetchWebTrackerResolution from "../queries/useFetchWebTrackerResolution";

const useStyles = makeStyles((theme) => ({
  cardHeader: {
    backgroundColor: theme.palette.grey[50],
    borderBottomColor: theme.palette.grey[200],
    borderBottomStyle: "solid",
    borderBottomWidth: 1,
    padding: theme.spacing(2),
  },
  cardContent: {
    padding: theme.spacing(2, 2),
  },
  actionButtons: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
  },
  confirmButton: {
    marginLeft: theme.spacing(2),
  },
}));

// resolutionTypeImperative returns a string representing the resolution type to
// be used when describing what the customer wants to do to the tracker
export function resolutionTypeImperative(
  resolutionType: WebTrackerResolutionType
) {
  switch (resolutionType) {
    case "marked_as_safe":
      return "Mark as Safe";
    case "marked_for_removal":
      return "Remove Tracker";
    case "replace_with_freshpaint":
      return "Replace with Freshpaint";
    case "signed_baa":
      return "Signed BAA";
    case "tracker_blocked":
      return "Block Tracker";
    case "tracker_first_party":
      return "Owned By My Organization";
    case "unresolved":
      return "Unresolved";
    default:
      // This is unlikely to ever occur, but we should handle it just in case.
      throw new Error(`Invalid resolution type: ${resolutionType}`);
  }
}

// resolutionTypeAdjective returns a string representing the resolution type to
// be used when describing what the customer has elected to do with the tracker
export function resolutionTypeAdjective(
  resolutionType: WebTrackerResolutionType
) {
  switch (resolutionType) {
    case "marked_as_safe":
      return "Marked as Safe";
    case "marked_for_removal":
      return "Marked for Removal";
    case "replace_with_freshpaint":
      return "Replace with Freshpaint";
    case "signed_baa":
      return "Signed BAA";
    case "tracker_blocked":
      return "Tracker Blocked";
    case "tracker_first_party":
      return "Owned By My Organization";
    case "unresolved":
      return "Unresolved";
    default:
      // This is unlikely to ever occur, but we should handle it just in case.
      throw new Error(`Invalid resolution type: ${resolutionType}`);
  }
}

function isHIPAASafeServerSideDestination(
  destination: SaaSDestination | RequestableSaasDestination | undefined
): boolean {
  const serverSideDisabled =
    destination?.connectionMode === "client" && !destination.supportsServerSide;
  return !!(destination && !serverSideDisabled);
}

export type TrackerResolutionProps = {
  webTrackerReportStats: WebTrackerReportTrackerStats;
  webTracker: WebTracker;
  disableBAA: boolean;
  isResolutionLoading: boolean;
};

function TrackerResolution({
  webTracker,
  webTrackerReportStats,
  disableBAA,
  isResolutionLoading,
}: TrackerResolutionProps) {
  const classes = useStyles();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [
    newResolutionType,
    setNewResolutionType,
  ] = useState<WebTrackerResolutionType | null>(null);

  const trackerIdentifier = firstFoundDomain(webTrackerReportStats);
  const alert = useAlert();

  const onAddResolutionSuccess = () => {
    alert.success("Update successful");
  };

  const addWebTrackerResolutionMutation = useAddWebTrackerResolution(
    onAddResolutionSuccess
  );

  const { data } = useFetchWebTrackerResolution(trackerIdentifier);

  const handleChangeResolution = (
    newResolutionType: WebTrackerResolutionType
  ) => {
    const currentResolutionType =
      data?.webTrackerResolution?.resolutionType || "unresolved";
    if (
      currentResolutionType !== undefined &&
      newResolutionType === currentResolutionType
    ) {
      return;
    }

    setNewResolutionType(newResolutionType);
    setIsEditing(true);
  };

  const handleConfirmChangeResolution = () => {
    if (newResolutionType === null) {
      // This is unlikely to ever occur, but we should handle it just in case.
      throw new Error("newResolutionType should not be null");
    }

    addWebTrackerResolutionMutation.mutate({
      note: data?.webTrackerResolution?.note || "",
      trackerDomain: trackerIdentifier,
      trackerName: webTracker.name,
      oldResolutionType:
        data?.webTrackerResolution?.resolutionType ?? "unresolved",
      newResolutionType: newResolutionType,
    });

    setIsEditing(false);
    setNewResolutionType(null);
  };

  const handleCancelChangeResolution = () => {
    setIsEditing(false);
    setNewResolutionType(null);
  };

  const allSaasDestinations = useAllSaasDestinations();

  const destination = useMemo(
    () => allSaasDestinations[webTracker.destinationSlug],
    [allSaasDestinations, webTracker.destinationSlug]
  );

  const hasHIPAASafeServerSideDestination = isHIPAASafeServerSideDestination(
    destination
  );
  const hasFreshpaintIntegration =
    hasHIPAASafeServerSideDestination ||
    hasVideoProxy(webTracker) ||
    isMaps(webTracker);

  const isConfirmBtnDisabled =
    newResolutionType === "replace_with_freshpaint" &&
    !hasFreshpaintIntegration;

  return (
    <Card>
      <CardHeader className={classes.cardHeader} title="Resolution" />
      <CardContent className={classes.cardContent}>
        {isEditing && newResolutionType !== null ? (
          <>
            <TrackerResolutionExplanation
              webTrackerResolutionType={newResolutionType}
              hasFreshpaintIntegration={hasFreshpaintIntegration}
              trackerName={webTracker.name}
            />
            <Typography>
              Are you sure you want to change the resolution from{" "}
              <strong>
                {resolutionTypeAdjective(
                  data?.webTrackerResolution?.resolutionType || "unresolved"
                )}
              </strong>{" "}
              to <strong>{resolutionTypeAdjective(newResolutionType)}</strong>?
            </Typography>

            <div className={classes.actionButtons}>
              <Button
                color="primary"
                variant="outlined"
                onClick={handleCancelChangeResolution}
              >
                Cancel
              </Button>
              <Button
                disabled={isConfirmBtnDisabled}
                className={classes.confirmButton}
                color="primary"
                variant="contained"
                onClick={handleConfirmChangeResolution}
              >
                Confirm
              </Button>
            </div>
          </>
        ) : (
          <>
            <TrackerResolutionContent
              isEditing={isEditing}
              isLoading={
                addWebTrackerResolutionMutation.isLoading || isResolutionLoading
              }
              webTrackerResolution={data?.webTrackerResolution ?? undefined}
              user={data?.user}
              onChange={handleChangeResolution}
              disableBAA={disableBAA}
              trackerName={webTracker.name}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default TrackerResolution;
